import React from 'react';
import PropTypes from 'prop-types';

import {Box, Paper, Stack, Typography} from 'views/components';
import {Translate} from 'views/module';

const columnHeaderSx = {
  fontSize: '10px',
  fontWeight: 600,
  letterSpacing: '0.04em',
  color: '#A8A49E',
  textAlign: 'right',
  width: 92,
  flexShrink: 0,
};

const countCellSx = {
  fontSize: '12px',
  fontWeight: 600,
  color: '#1A1916',
  textAlign: 'right',
  width: 92,
  flexShrink: 0,
};

const TOKEN_COLUMNS = [
  {key: 'inputTokens', headerExpression: 'metrics.AgentReportCard.TokenSpend.Input'},
  {key: 'cacheReadInputTokens', headerExpression: 'metrics.AgentReportCard.TokenSpend.CacheRead'},
  {key: 'cacheCreationInputTokens', headerExpression: 'metrics.AgentReportCard.TokenSpend.CacheWrite'},
  {key: 'outputTokens', headerExpression: 'metrics.AgentReportCard.TokenSpend.Output'},
];

// Rollups written before token tracking carry no tokenSpend, so the whole
// section stays hidden until at least one fetched day reports spend fields.
const TokenSpendSection = (props) => {
  const {
    isLoading,
    errorMessage,
    hasTokenData,
    spendRows,
  } = props;

  if (isLoading || errorMessage || !hasTokenData) {
    return null;
  }

  return (
    <Box>
      <Typography
        sx={{
          fontSize: '11px',
          fontWeight: 600,
          lineHeight: '14px',
          letterSpacing: '0.04em',
          color: '#A8A49E',
          mb: 2,
        }}
      >
        <Translate expression='metrics.AgentReportCard.TokenSpendSection' />
      </Typography>
      <Paper
        sx={{
          p: 3,
          borderRadius: '12px',
          border: '1px solid #E5E5E5',
          boxShadow: 'none',
        }}
      >
        <Typography
          sx={{
            fontSize: '16px',
            fontWeight: 600,
            lineHeight: '21px',
            color: '#1A1916',
            mb: 0.75,
          }}
        >
          <Translate expression='metrics.AgentReportCard.TokenSpend.Title' />
        </Typography>
        <Typography
          sx={{
            fontSize: '12px',
            lineHeight: '16px',
            color: '#A8A49E',
            mb: 3,
          }}
        >
          <Translate expression='metrics.AgentReportCard.TokenSpend.Subtitle' />
        </Typography>
        <Stack spacing={1.5}>
          <Stack direction='row' alignItems='center' spacing={1.25}>
            <Typography
              sx={{
                ...columnHeaderSx,
                flex: 1,
                width: 'auto',
                textAlign: 'left',
              }}
            >
              <Translate expression='metrics.AgentReportCard.TokenSpend.Source' />
            </Typography>
            {
              TOKEN_COLUMNS.map((column) => (
                <Typography key={column.key} sx={columnHeaderSx}>
                  <Translate expression={column.headerExpression} />
                </Typography>
              ))
            }
          </Stack>
          {
            spendRows.map((spendRow) => (
              <Stack key={spendRow.key} direction='row' alignItems='center' spacing={1.25}>
                <Typography
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    fontSize: '11px',
                    lineHeight: '14px',
                    color: '#6B6761',
                    fontWeight: spendRow.emphasize ? 600 : 400,
                  }}
                >
                  {spendRow.label}
                </Typography>
                {
                  TOKEN_COLUMNS.map((column) => (
                    <Typography
                      key={column.key}
                      sx={{
                        ...countCellSx,
                        color: spendRow.emphasize ? '#1A1916' : '#6B6761',
                      }}
                    >
                      {(spendRow.spend[column.key] || 0).toLocaleString()}
                    </Typography>
                  ))
                }
              </Stack>
            ))
          }
        </Stack>
      </Paper>
    </Box>
  );
};

TokenSpendSection.propTypes = {
  isLoading: PropTypes.bool.isRequired,
  errorMessage: PropTypes.string.isRequired,
  hasTokenData: PropTypes.bool.isRequired,
  spendRows: PropTypes.array.isRequired,
};

export default React.memo(TokenSpendSection);
